/*
 * @Author: Christion.Young
 * @Date: 2025-08-14 14:00:00
 * @LastEditors: Christion.Young
 * @LastEditTime: 2025-08-14 14:00:00
 * @Description: 统一API管理 - 所有接口的统一入口
 */

import { get, post, put, delet } from "@/utils";
import { encryption } from "@/utils/crypto";

/**
 * 地域参数自动注入辅助
 * 逻辑：
 *  1. 从 Vuex 读取 user/regionFilterEnabled 与 user/selectedCityName
 *  2. 若启用地域筛选且存在已选择城市中文名，则克隆 data 并追加 address 字段
 *  3. 可通过 overrideCity 强制覆盖城市（测试或特殊调用场景）
 *  4. 失败静默，不中断业务
 * @param {Object} data 原始查询参数
 * @param {string} [overrideCity] 手动指定城市中文名
 * @returns {Object} 带 address 或原样的参数对象
 */
function withRegionAddress(data, overrideCity) {
  try {
    const store = require("@/store").default;
    const enabled = store.getters["user/regionFilterEnabled"]; // 是否启用地域筛选
    const cityName = overrideCity || store.getters["user/selectedCityName"]; // 城市中文名
    if (enabled && cityName) {
      return { ...(data || {}), address: cityName };
    }
  } catch (e) {
    // 静默处理，避免在构建或SSR等环境报错
    console.warn("withRegionAddress 注入失败:", e?.message);
  }
  return data || {};
}

/**
 * 构建带参数的URL
 * @param {string} baseURL - 基础URL
 * @param {Record<string, any>} params - 参数对象
 * @returns {any} 带参数的完整URL
 */
export function buildUrlWithParams(baseURL, params = {}) {
  if (typeof params !== "object" || params === null) {
    return baseURL;
  }

  const queryString = Object.entries(params)
    .filter(([key, value]) => value !== undefined && value !== null)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join("&");

  if (queryString) {
    const separator = baseURL.includes("?") ? "&" : "?";
    return `${baseURL}${separator}${queryString}`;
  }

  return baseURL;
}

/**
 * 认证相关API
 */
export const authAPI = {
  /**
   * OAuth2密码模式登录
   * @param {Object} data - 登录数据
   * @param {string} data.username - 用户名
   * @param {string} data.password - 密码
   * @returns {Promise} 登录结果
   */
  login(data) {
    // OAuth2客户端配置（写死在代码中）
    const OAUTH2_CLIENT = "pig:pig";
    const PWD_ENC_KEY = "pigxpigxpigxpigx";

    // 构造Basic认证头
    const basicAuth = "Basic " + window.btoa(OAUTH2_CLIENT);

    // 密码加密
    const encPassword = encryption(data.password, PWD_ENC_KEY);

    // 构造请求参数
    const { username } = data;

    // URL参数
    const params = {
      username,
      randomStr: "blockPuzzle",
      code: "",
      grant_type: "password",
      scope: "server",
    };

    // 请求体数据
    const formData = new URLSearchParams();
    formData.append("password", encPassword);

    return new Promise((resolve, reject) => {
      // 使用原生axios进行OAuth2请求
      const axios = require("axios");

      axios({
        url: "/api/auth/oauth2/token",
        method: "post",
        params: params,
        data: formData,
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/x-www-form-urlencoded",
          skipToken: "true",
        },
        timeout: 50000,
      })
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  /**
   * 刷新Token
   * @param {string} refreshToken - 刷新令牌
   * @returns {Promise} 新的token
   */
  refreshToken(refreshToken) {
    // OAuth2客户端配置（写死在代码中）
    const OAUTH2_CLIENT = "pig:pig";
    const basicAuth = "Basic " + window.btoa(OAUTH2_CLIENT);

    const params = {
      grant_type: "refresh_token",
      refresh_token: refreshToken,
      scope: "server",
    };

    return new Promise((resolve, reject) => {
      const axios = require("axios");

      axios({
        url: "/api/auth/oauth2/token",
        method: "post",
        params: params,
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/x-www-form-urlencoded",
          skipToken: "true",
        },
      })
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  /**
   * 退出登录
   * @returns {Promise} 登出结果
   */
  logout() {
    return delet("auth/token/logout");
  },

  /**
   * 获取用户信息
   * @returns {Promise} 用户信息
   */
  getUserInfo() {
    return get("admin/user/info");
  },
};

/**
 * 平台相关API（无需权限）
 */
export const platformAPI = {
  /**
   * 关于平台
   * @param {Object} data - 查询参数
   * @returns {Promise} 平台信息
   */
  getPlatformInfo(data) {
    return get("admin/platform/page", data);
  },

  /**
   * 获取轮播图
   * @param {Object} data - 查询参数
   * @returns {Promise} 轮播图列表
   */
  getBannerList(data) {
    return get("admin/banner/page", data);
  },
};

/**
 * 服务相关API
 */
export const serviceAPI = {
  /**
   * 获取一级服务分类
   * @param {Object} data - 查询参数
   * @returns {Promise} 服务分类列表
   */
  getServiceTypeMaster(data) {
    return get("admin/serviceTypeMaster/page", data);
  },

  /**
   * 获取二级服务分类
   * @param {Object} data - 查询参数
   * @returns {Promise} 二级分类列表
   */
  getServiceTypeDetail(data) {
    return get("admin/serviceTypeDetail/page", data);
  },

  /**
   * 获取服务列表（无需登录）
   * @param {Object} data - 查询参数
   * @param {string} [data.address] - 城市名称（通常无需手动传入，若 store 中 regionFilterEnabled=true 且已选择城市，会自动注入）
   * @returns {Promise} 服务列表
   */
  getServiceListNoToken(data) {
    const payload = withRegionAddress(data);
    return get("admin/serviceManagement/pageNoToken", payload);
  },

  /**
   * 获取服务列表（支持服务分类筛选）
   * @param {Object} data - 查询参数
   * @param {string} [data.address] - 城市名称（通常无需手动传入，若 store 中 regionFilterEnabled=true 且已选择城市，会自动注入）
   * @returns {Promise} 服务列表
   */
  getServiceList(data) {
    const payload = withRegionAddress(data);
    return get("admin/serviceManagement/pageNoToken", payload);
  },

  /**
   * 获取服务详情（无需登录）
   * @param {string} id - 服务ID
   * @returns {Promise} 服务详情
   */
  getServiceDetailNoToken(id) {
    return get(`admin/serviceManagement/noToken/${id}`);
  },

  /**
   * 服务信息-商品和人员
   * @param {any} data - 服务ID
   * @returns {Promise} 服务关联信息
   */
  serviceRelations(data) {
    return get(`admin/serviceManagement/relations/${data}`);
  },

  /**
   * 获取评价
   * @param {Object} data - 查询参数
   * @param {number} data.current - 页码，从1开始
   * @param {number} data.size - 每页数量
   * @param {number} data.status - 评价状态：1-已发布/有效的评价
   * @param {string} data.serviceId - 服务ID，用于获取特定服务的评价
   * @returns {Promise} 评价列表
   */
  getReview(data) {
    return get("admin/review/page", data);
  },
};

/**
 * 商家相关API
 */
export const merchantAPI = {
  /**
   * 获取推荐商家
   * @param {Object} data - 查询参数
   * @param {string} [data.address] - 城市名称（自动注入，除非强制传入覆盖）
   * @returns {Promise} 推荐商家列表
   */
  getMerchantRecommend(data) {
    // 支持地域注入（address）
    const payload = withRegionAddress(data);
    return get("admin/merchantRecommend/page", payload);
  },

  /**
   * 获取商家分页（已登录专用，更完整数据）
   * @param {Object} data - 查询参数 { current, size, auditStatus, descs, ascs, name, service }
   * @returns {Promise} 商家分页数据
   */
  getMerchantPage(data) {
    // 已登录接口不需要地域自动注入（若业务需要可自行加）
    return get("admin/merchant/page", data);
  },

  /**
   * 获取商家信息（无需登录，支持距离参数）
   * @param {string} data - 商家ID
   * @param {Object} distance - 距离参数对象
   * @returns {Promise} 商家信息
   */
  getMerchantID(data, distance) {
    return get(buildUrlWithParams(`admin/merchant/${data}`, distance));
  },

  /**
   * 获取商家员工分页（无需登录，支持URL参数）
   * @param {Object} data - 查询参数对象
   * @returns {Promise} 员工分页列表
   */
  getMerchantStaffPage(data) {
    return get(buildUrlWithParams(`admin/merchant/staff/page`, data));
  },
};

/**
 * 消息相关API
 */
export const messageAPI = {
  /**
   * 获取消息统计信息 - 按照接口文档实现
   * @returns {Promise} 消息统计数据
   */
  messageStats() {
    return get("admin/message/stats");
  },

  /**
   * 获取聊天列表 - 按照接口文档实现
   * @returns {Promise} 聊天列表数据
   */
  messageChatList() {
    return get("admin/message/chat/list");
  },

  /**
   * 获取聊天历史记录 - 按照接口文档实现
   * @param {string} fromUserId - 发送方用户ID
   * @param {string} toUserId - 接收方用户ID
   * @param {boolean} loading - 是否显示loading，默认true
   * @returns {Promise} 聊天历史数据
   */
  messageChatHistory(fromUserId, toUserId, loading = true) {
    return get(
      `admin/message/chat/between/${fromUserId}/${toUserId}`,
      {},
      loading
    );
  },

  /**
   * 获取消息分页查询 - 按照接口文档实现
   * @param {Object} data - 查询参数
   * @param {number} data.current - 当前页码，从1开始
   * @param {number} data.size - 每页数量
   * @param {string} data.toUserId - 筛选接收方用户ID（可选）
   * @param {string} data.fromUserId - 筛选发送方用户ID（可选）
   * @param {string} data.readStatus - 筛选阅读状态（可选）
   * @param {string} data.msgType - 筛选消息类型（可选）
   * @param {string} data.startTime - 开始时间筛选（可选）
   * @param {string} data.endTime - 结束时间筛选（可选）
   * @param {string} data.keyword - 消息内容关键词搜索（可选）
   * @returns {Promise} 消息分页数据
   */
  messageGet(data) {
    return get("admin/message/page", data);
  },

  /**
   * 修改消息 - 按照接口文档实现
   * @param {Object} data - 消息数据
   * @param {string} data.id - 必需：消息ID
   * @param {string} data.readStatus - 可选：阅读状态
   * @param {string} data.content - 可选：修改消息内容
   * @param {boolean} data.isRevoked - 可选：是否撤回消息
   * @param {object} data.extraData - 可选：扩展数据
   * @returns {Promise} 修改结果
   */
  messageAmend(data) {
    return put("admin/message", data);
  },

  /**
   * 新增消息 - 按照接口文档实现
   * @param {Object} data - 消息数据
   * @param {string} data.fromUserId - 必需：发送方用户ID
   * @param {string} data.fromUserName - 必需：发送方用户昵称
   * @param {string} data.toUserId - 必需：接收方用户ID
   * @param {string} data.toUserName - 必需：接收方用户昵称
   * @param {string} data.content - 必需：消息内容
   * @param {string} data.msgType - 必需：消息类型
   * @param {string} data.orderId - 可选：关联订单ID
   * @param {string} data.page - 必需：消息来源页面
   * @param {string} data.readStatus - 必需：阅读状态，默认'0'未读
   * @param {object} data.extraData - 可选：扩展数据
   * @returns {Promise} 新增结果
   */
  messageAdd(data) {
    return post("admin/message", data);
  },
};

/**
 * 用户相关API
 */
export const userAPI = {
  /**
   * 获取用户订单分页列表
   * @param {Object} data - 查询参数
   * @param {number} data.current - 当前页码
   * @param {number} data.size - 每页数量
   * @param {string} data.orderStatus - 订单状态 (1-6对应不同状态)
   * @param {string} data.orderName - 搜索的订单名称
   * @param {string} data.createId - 创建用户ID
   * @param {string} data.timeSort - 时间排序方式
   * @returns {Promise} 订单分页数据
   */
  getOrdersPage(data) {
    return get("admin/orders/page", data);
  },

  /**
   * 获取平台佣金结算记录分页（用于统计商户成交订单数）
   * @param {Object} data - 查询参数 { current, size, merchantId }
   * @returns {Promise} 佣金结算分页数据
   */
  platformCommissionPage(data) {
    return get("admin/platform/commission/page", data);
  },

  /**
   * 更新用户信息 (通用接口)
   * @param {Object} obj - 用户数据对象
   * @param {string} obj.userId - 用户ID
   * @param {string} obj.username - 用户名
   * @param {string} obj.password - 密码（可选，修改密码时传入）
   * @param {string} obj.phone - 手机号
   * @param {string} obj.nickname - 昵称
   * @param {string} obj.email - 邮箱
   * @returns {Promise} 更新结果
   */
  putObj(obj) {
    return put("admin/user", obj);
  },
};

/**
 * 兼容旧版本的API导出
 * @deprecated 建议使用具体的API分类，如 authAPI, serviceAPI 等
 */
export const noPermissionRequired = {
  getPlatformInfo: platformAPI.getPlatformInfo,
  getServiceTypeMaster: serviceAPI.getServiceTypeMaster,
  getServiceTypeDetail: serviceAPI.getServiceTypeDetail,
  getBannerList: platformAPI.getBannerList,
  getMerchantRecommend: merchantAPI.getMerchantRecommend,
  getServiceListNoToken: serviceAPI.getServiceListNoToken,
  getServiceList: serviceAPI.getServiceList, // 新增支持服务分类筛选的接口
  getServiceDetailNoToken: serviceAPI.getServiceDetailNoToken,
  serviceRelations: serviceAPI.serviceRelations,
  getReview: serviceAPI.getReview,
  getMerchantID: merchantAPI.getMerchantID,
  getMerchantStaffPage: merchantAPI.getMerchantStaffPage,
};

/**
 * 统一API对象 - 推荐使用方式
 */
export default {
  // 认证相关
  auth: authAPI,

  // 平台相关
  platform: platformAPI,

  // 服务相关
  service: serviceAPI,

  // 商家相关
  merchant: merchantAPI,

  // 消息相关
  message: messageAPI,

  // 用户相关
  user: userAPI,

  // 兼容性支持
  noPermissionRequired,
};
